import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useEffect, useState } from 'react'
import { IoAddSharp, IoClose } from 'react-icons/io5'
import { FiChevronDown } from 'react-icons/fi'
import { Loader } from '../../../shared/Loader'
import { SuccessIcon } from '../../../shared/SuccessIcon'
import { useGetKycsQuery } from '../../../features/account/kycSlice'
import { selectCurrentUser, updateCredentials } from '../../../features/auth/authSlice'
import { useDispatch, useSelector } from 'react-redux'
import { MdVerifiedUser } from 'react-icons/md'
import { FaUserShield } from 'react-icons/fa'
import { UserInformation } from './User Information'
import { useCurrentUserQuery } from '../../../features/chat/chatSlice'
import { NigerianKycForm } from './NigerianKycForm'
import { CountrySelectionModal, Country } from './CountrySelectionModal'

export const Kyc = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState<'country' | 'init' | 'nigerian' | 'Success' | 'Verified'>('country')
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false)
  const dispatch = useDispatch()

  const { data: kycs, isLoading, isError } = useGetKycsQuery()

  const user = useSelector(selectCurrentUser);
  const { data: currentUser, isLoading: isCurrentUserLoading } = useCurrentUserQuery({
    time: Date.now()
  })

  useEffect(() => {
    if (user?.isKYCVerified) {
      setCurrentStep('Verified')
    }
  }, [user])

  useEffect(() => {
    if (currentUser && user && user?.isKYCVerified != currentUser?.isKYCVerified) {
      // Update user state
      dispatch(updateCredentials({
        user: currentUser
      }))
    }
  }, [currentUser])

  const handleContinue = () => {
    if (!selectedCountry) return

    if (selectedCountry.code === 'NG') {
      setCurrentStep('nigerian')
    } else {
      setCurrentStep('init')
    }
  }

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country)
    setIsCountryModalOpen(false)
  }

  function _renderStepContent(step: string) {
    switch (step) {
      case 'country':
        return (
          <div className='flex flex-col items-center gap-6 py-12'>
            <div className='text-center'>
              <h3 className='text-lg text-white mb-2'>
                Select Your Country
              </h3>
              <p className='text-sm text-white/60'>
                Choose your country to proceed with identity verification
              </p>
            </div>

            {/* Country Dropdown */}
            <div className='w-full max-w-sm'>
              <button
                type='button'
                onClick={() => setIsCountryModalOpen(true)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 ${
                  selectedCountry
                    ? 'border-primary bg-primary/10'
                    : 'border-gray-600 bg-gray-700/30 hover:border-gray-500'
                }`}
              >
                {selectedCountry ? (
                  <div className='flex items-center gap-3'>
                    <span className='text-2xl'>{selectedCountry.flag}</span>
                    <span className='text-white font-medium'>{selectedCountry.name}</span>
                  </div>
                ) : (
                  <span className='text-gray-400'>Select your country</span>
                )}
                <FiChevronDown className='text-xl text-gray-400' />
              </button>
            </div>

            <button
              className='btn-primary py-3 px-8 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed'
              onClick={handleContinue}
              disabled={!selectedCountry}
            >
              Continue
            </button>

            {/* Country Selection Modal */}
            <CountrySelectionModal
              isOpen={isCountryModalOpen}
              onClose={() => setIsCountryModalOpen(false)}
              onSelect={handleCountrySelect}
              selectedCountry={selectedCountry}
            />
          </div>
        )

      case 'nigerian':
        return (
          <div className='mt-5 px-4'>
            <NigerianKycForm setCurrentStep={setCurrentStep} />
          </div>
        )

      case 'init':
        return (
          <div className='flex flex-col items-center gap-2 mt-5'>
            <div className='mt-3 w-full max-w-sm'>
              <UserInformation
                setCurrentStep={setCurrentStep}
                country={selectedCountry?.name || 'Other'}
              />
            </div>
          </div>
        )

      case 'Success':
        return (
          <div className='relative py-24 flex flex-col items-center'>
            <div className='flex flex-col items-center gap-4'>
              <SuccessIcon />
              <p className='text-xl font-light text-white/40'>
                Document Submitted Successfully
              </p>
            </div>

            <button
              className='btn-primary py-2 rounded-lg w-full max-w-md mt-12'
              onClick={async () => {
                setIsModalOpen(false)
                setTimeout(() => {
                  setCurrentStep('country')
                  setSelectedCountry(null)
                }, 1000)
              }}
            >
              Done
            </button>
          </div>
        )
      case 'Verified':
        return (
          <div className='relative py-24 flex flex-col items-center'>
            <div className='flex flex-col items-center gap-4'>
              <SuccessIcon />
              <p className='text-xl font-light text-white/40'>
                You are already verified
              </p>
            </div>
          </div>
        )
    }
  }

  return (
    <div className='w-full relative py-8'>
      {isLoading ? (
        <div className='w-full h-[70vh] flex justify-center items-center'>
          <Loader />
        </div>
      ) : kycs?.length > 0 ? (
        <div className='w-full md:px-4 py-5 md:py-3 bg-black'>
          <div className='flex items-center justify-between pr-5'>
            <h1 className='text-xl text-primary font-semibold pl-5'>
              All submissions
            </h1>
            {
              !user?.isKYCVerified && (
                <button
                  className='transition-all duration-200 flex items-center gap-2 rounded-full px-4 py-2 bg-white/90 hover:bg-white hover:text-black text-sm'
                  onClick={() => setIsModalOpen(true)}
                >
                  <span>
                    <IoAddSharp className='text-lg text-black' />
                  </span>{' '}
                  Submit New Document
                </button>
              )
            }

          </div>
          <div className='overflow-x-auto'>
            <table className='relative mt-8 w-full text-sm text-left text-gray-400'>
              <thead className='text-xs uppercase bg-gray-700 text-gray-300'>
                <tr>
                  <th scope='col' className='py-3 px-6'>
                    Document Type
                  </th>
                  <th scope='col' className='py-3 px-6'>
                    Submition Date
                  </th>
                  <th scope='col' className='py-3 px-6'>
                    Verification Status
                  </th>
                  <th scope='col' className='py-3 px-6'>
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody>
                {kycs?.map((bank: any, index: number) => (
                  <tr key={bank._id || index} className='border-b bg-transparent border-gray-700'>
                    <th
                      scope='row'
                      className='py-4 px-6 font-medium whitespace-nowrap text-white'
                    >
                      {bank?.documentType}
                    </th>

                    <td className='py-4 px-6'>{new Date(bank?.createdAt).toUTCString()}</td>
                    <td className='py-4 px-6'>
                      {bank?.status}
                    </td>
                    <td className='py-4 px-6'>
                      {bank?.comment || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : !user?.isKYCVerified ? (
        <div className='max-w-md mx-auto flex justify-center items-center pt-20'>
          <div className='flex flex-col items-center gap-2'>
            <FaUserShield className='box-content p-4 rounded-md text-5xl bg-gray-600 text-white/20' />

            <p className='text-sm text-white/30 font-light text-center'>
              Verify Your Identity to increase withdrawal limit
            </p>

            <button
              className='transition-all duration-200 border border-white/80 rounded-full w-64 px-4 py-3 hover:bg-white/80 text-white/60 hover:text-black text-sm mt-4'
              onClick={() => setIsModalOpen(true)}
            >
              Start Verification
            </button>
          </div>
        </div>
      ) : (
        <div className='max-w-md h-[60vh] mx-auto flex justify-center items-center pt-20'>
          <div className='flex flex-col items-center gap-2'>
            <MdVerifiedUser className='box-content p-4 rounded-md text-5xl bg-green-600 text-white/20' />

            <p className='text-sm text-white/30 font-light text-center'>
              Your Identity is already verified
            </p>
          </div>
        </div>
      )


      }

      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={() => setIsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black/40 backdrop-blur-sm' />
          </Transition.Child>

          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <Dialog.Panel className='relative font-dashboard w-full max-w-3xl min-h-[500px] transform overflow-hidden rounded-2xl bg-[#161819] px-4 pt-4 pb-10 text-left align-middle shadow-xl transition-all'>
                  <div className='relative pt-2 pb-4'>
                    <h3 className='text-xl text-white/40 font-light text-center'>
                      Identity Verification
                    </h3>
                    <IoClose
                      className='text-2xl text-white absolute right-3 top-3 cursor-pointer'
                      onClick={() => setIsModalOpen(false)}
                    />
                  </div>
                  <hr className='border-white/10' />

                  {_renderStepContent(currentStep)}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
